<template>
  <div v-if="isEligible" class="bpm-address-ttr-tax-exemption">
    <zm-alert type="info" show-icon :closable="false">
      <template v-if="ttrTaxExempt === taxStatusList.EXEMPT">
        <div>
          <strong>
            {{ __$t('billing.tax_exemption.ttr.tax_status_label', [getTaxStatusLabel(taxStatusList.EXEMPT)]) }}
          </strong>
        </div>
      </template>
      <template v-else-if="ttrTaxExempt === taxStatusList.PENDING">
        <div>
          <strong>
            {{ __$t('billing.tax_exemption.ttr.tax_status_label', [getTaxStatusLabel(taxStatusList.PENDING)]) }}
          </strong>
        </div>
        <div>
          {{ __$t('billing.tax_exemption.ttr.status_pending_desc') }}
        </div>
      </template>
      <template v-else-if="ttrTaxExempt === taxStatusList.RETRIEVING">
        <div>
          <strong>
            {{ __$t('billing.tax_exemption.ttr.tax_status_label', [getTaxStatusLabel(taxStatusList.RETRIEVING)]) }}
          </strong>
        </div>
        <div>
          {{ __$t('billing.tax_exemption.ttr.status_retrieving_desc') }}
        </div>
      </template>
      <template v-else>
        <zm-button type="link" @click.stop="openTtrPortal" class="bpm-address-ttr-tax-exemption__action">
          {{ __$t('billing.tax_exemption.ttr.link') }}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <path
              d="M.985 3.5a2.5 2.5 0 0 1 2.5-2.5H6.5a.5.5 0 0 0 0-1H3.485a3.5 3.5 0 0 0-3.5 3.5v9a3.5 3.5 0 0 0 3.5 3.5H12.5a3.5 3.5 0 0 0 3.5-3.5v-3a.5.5 0 0 0-1 0v3a2.5 2.5 0 0 1-2.5 2.5H3.485a2.5 2.5 0 0 1-2.5-2.5v-9Z"
            />
            <path
              d="M10.5 0a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5a.5.5 0 0 0-.5-.5h-5Z"
            />
          </svg>
        </zm-button>
        <div>
          {{ __$t('billing.tax_exemption.ttr.link_desc') }}
        </div>
      </template>
      <!-- eslint-disable-next-line -->
      <zm-alert v-if="errorMsg" type="error" show-icon :closable="true" v-text="errorMsg" />
    </zm-alert>

    <zm-dialog
      v-if="inProgressDialogVisible"
      :title="__$t('billing.tax_exemption.ttr.in_progress_title')"
      :visible.sync="inProgressDialogVisible"
      class="bpm-address-ttr-tax-exemption__dialog"
      append-to-body
    >
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-html="__$t('billing.tax_exemption.ttr.in_progress_desc')"></p>
      <span slot="footer" class="dialog-footer">
        <zm-button @click="progressDialogCancelHandler">
          {{ __$t('billing.tax_exemption.ttr.in_progress_close_button') }}
        </zm-button>
      </span>
    </zm-dialog>

    <zm-dialog
      v-if="abandonDialogVisible"
      :title="__$t('billing.tax_exemption.ttr.quit_progress_title')"
      :visible.sync="abandonDialogVisible"
      class="bpm-address-ttr-tax-exemption__dialog"
      append-to-body
    >
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-html="__$t('billing.tax_exemption.ttr.quit_progress_desc')"></p>
      <span slot="footer" class="dialog-footer">
        <zm-button type="primary" @click="abandonProgressConfirmHandler">
          {{ __$t('billing.tax_exemption.ttr.quit_progress_confirm') }}
        </zm-button>
        <zm-button @click="abandonProgressCancelHandler">
          {{ __$t('billing.tax_exemption.ttr.quit_progress_cancel') }}
        </zm-button>
      </span>
    </zm-dialog>
  </div>
</template>

<script>
import {
  getTtrTaxExemptLinkForExitingCustomer,
  getTtrTaxExemptLinkForNewCustomer,
  getTtrTaxStatus
} from '../../apis/address'
import { ttrTaxStatusList } from '../../common/constant'
import { translateMixins } from '../../plugins/i18n'
import { APP_NAMESPACE } from '../../store'
import trackingMixin from '../mixins/trackingMixin'
import { mapGetters, mapMutations } from 'vuex'
import CryptoJS from 'crypto-js'

export default {
  name: 'ttrTaxExemption',
  mixins: [translateMixins, trackingMixin],
  props: {
    tracking: Function,
    selectedCountry: String,
    account: {
      type: Object
    }
  },
  data() {
    return {
      isEligible: false,
      taxStatusList: ttrTaxStatusList,
      errorMsg: null,
      ttrPortalWindow: null,
      ttrPortalTaxStatus: null,
      inProgressDialogVisible: false,
      abandonDialogVisible: false
    }
  },
  computed: {
    ...mapGetters(APP_NAMESPACE, ['addressInfo', 'taxInfo', 'isNewCustomer']),
    ttrTaxExempt() {
      return this.taxInfo.ttrTaxExempt
    }
  },
  methods: {
    ...mapMutations(APP_NAMESPACE, ['setTaxInfo']),
    setTtrTaxExempt(ttrTaxExempt) {
      if (ttrTaxExempt !== this.taxInfo.ttrTaxExempt) {
        this.setTaxInfo({ ...this.taxInfo, ttrTaxExempt })
      }
    },
    openTtrPortal() {
      // validate address fields, save to store, then open TTR Portal
      this.$emit('validateAndSave', {
        success: this.handleOpenTtrPortal
      })
      this.tracking && this.tracking('tax status - verify tax exemption status')
    },
    handleOpenTtrPortal() {
      this.setupTtrPortalCallbackFn()

      this.inProgressDialogVisible = false
      this.abandonDialogVisible = false
      this.errorMsg = null

      this.ttrPortalWindow = window.open('about:blank', 'zoom-tax-exemption-portal')
      if (this.ttrPortalWindow === null || typeof this.ttrPortalWindow === 'undefined') {
        this.errorMsg = this.__$t('billing.pop_up_blocked')
        return
      }

      this.TTRWindowCheck = setInterval(() => {
        if (this.ttrPortalWindow.closed) {
          if (!this.ttrPortalTaxStatus) {
            this.setTtrTaxExempt(this.taxStatusList.NON_EXEMPT)
            this.updateStatusIfInitialized()
          }
          clearInterval(this.TTRWindowCheck)
          this.inProgressDialogVisible = false
          this.abandonDialogVisible = false
        }
      }, 1000)

      this.ttrPortalTaxStatus = null
      this.inProgressDialogVisible = true
      this.setTtrTaxExempt(this.taxStatusList.RETRIEVING)
      this.isNewCustomer ? this.handlerTaxExemptForNewCustomer() : this.handlerTaxExemptForExistingCustomer()
    },
    handlerTaxExemptForNewCustomer() {
      getTtrTaxExemptLinkForNewCustomer(this.getContactInfo())
        .then(({ portal_url: portalUrl }) => {
          this.ttrPortalWindow.location.href = portalUrl
        })
        .catch((response) => {
          this.handleTaxExemptException(response)
        })
    },
    handlerTaxExemptForExistingCustomer() {
      getTtrTaxExemptLinkForExitingCustomer()
        .then(({ portal_url: portalUrl }) => {
          const decrypted = CryptoJS.AES.decrypt(portalUrl, CryptoJS.enc.Utf8.parse('encryptPortalUrl'), {
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.Pkcs7
          })
          this.ttrPortalWindow.location.href = decrypted.toString(CryptoJS.enc.Utf8)
        })
        .catch((response) => {
          this.handleTaxExemptException(response)
        })
    },
    handleTaxExemptException(response) {
      this.errorMsg = response?.responseJSON?.errorMessage || this.__$t('billing.unknown_error')
      this.setTtrTaxExempt(this.taxStatusList.NON_EXEMPT)
      clearInterval(this.TTRWindowCheck)
      this.inProgressDialogVisible = false
      this.ttrPortalWindow.close()
    },
    getContactInfo() {
      const data = {
        first_name: this.account.firstName || null,
        last_name: this.account.lastName || null,
        email: this.account.email || null,
        phone: this.account.phone || null,
        companyORorg_name: this.addressInfo.companyName || null,
        address: this.addressInfo.address1 || null,
        aptSuite: this.addressInfo.address2 || null,
        city: this.addressInfo.city || null,
        state: this.addressInfo.state || null,
        zip: this.addressInfo.zip || null,
        country_region: this.addressInfo.country || null
      }

      // remove empty items
      Object.keys(data).forEach(function (key) {
        if (data[key] === null) {
          delete data[key]
        }
      })

      return data
    },
    progressDialogCancelHandler() {
      this.inProgressDialogVisible = false
      setTimeout(() => {
        this.abandonDialogVisible = true
      }, 200)
    },
    abandonProgressConfirmHandler() {
      this.setTtrTaxExempt(this.taxStatusList.NON_EXEMPT)
      this.abandonDialogVisible = false
      this.inProgressDialogVisible = false
      this.ttrPortalWindow && this.ttrPortalWindow.close()
    },
    abandonProgressCancelHandler() {
      this.abandonDialogVisible = false
      setTimeout(() => {
        this.inProgressDialogVisible = true
      }, 200)
    },
    setupTtrPortalCallbackFn() {
      // called from the /buy/ttr_accepted page that TTR Portal returns to when completed
      window.ZBilling = window.ZBilling || { TaxExemption: null }
      window.ZBilling.TaxExemption = window.ZBilling.TaxExemption || {}
      window.ZBilling.TaxExemption.updateStatus = this.updateStatus
    },
    updateStatus(status = null, showIncompleteError = false) {
      this.errorMsg = ''

      switch (status) {
        case this.taxStatusList.EXEMPT:
        case this.taxStatusList.PENDING:
        case this.taxStatusList.RETRIEVING:
          this.ttrPortalTaxStatus = status
          this.setTtrTaxExempt(status)
          break
        default:
          this.setTtrTaxExempt(this.taxStatusList.NON_EXEMPT)
          if (showIncompleteError) {
            this.errorMsg = this.__$t('billing.tax_exemption.ttr.status_incomplete')
          }
      }
    },
    getTaxStatusLabel(status) {
      switch (status) {
        case this.taxStatusList.EXEMPT:
          return this.__$t('billing.tax_exemption.ttr.status_exempt')
        case this.taxStatusList.PENDING:
          return this.__$t('billing.tax_exemption.ttr.status_pending')
        case this.taxStatusList.RETRIEVING:
          return this.__$t('billing.tax_exemption.ttr.status_retrieving')
        case this.taxStatusList.NON_EXEMPT:
          return this.__$t('billing.tax_exemption.ttr.status_nonexempt')
      }
    },
    updateStatusIfInitialized() {
      this.setTtrTaxExempt(this.taxStatusList.RETRIEVING)
      getTtrTaxStatus()
        .then(({ tax_status: taxStatus }) => {
          this.updateStatus(taxStatus)
        })
        .catch((errorMsg) => {
          this.errorMsg = errorMsg
          this.updateStatus()
        })
    }
  },
  watch: {
    selectedCountry: {
      handler(country) {
        if (country) {
          // TTR Tax Exemption only eligible for countries US and CA
          this.isEligible = country === 'US' || country === 'CA'
          if (!this.isEligible) {
            this.setTtrTaxExempt(this.taxStatusList.NON_EXEMPT)
          }
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.bpm-address-ttr-tax-exemption {
  margin: 30px 0 20px;

  &__action {
    .zm-button__slot {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    svg {
      width: 12px;
      height: 12px;
      margin-left: 5px;
      fill: currentColor;
    }
  }

  &__dialog {
    & > zm-dialog {
      width: 100%;
      max-width: 600px;
    }
  }
}
.dialog-footer {
  padding-right: 16px;
}
</style>
